import { useState, useEffect } from "react";

import { useFetcher, useNavigation, useRevalidator } from "@remix-run/react";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "~/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";

import type { CommitmentWithDetails } from "../types";

// AIDEV-NOTE: Type defined outside component to avoid re-rendering loop with useFetcher
type FulfillmentFetcherData = { error?: string; success?: boolean };

// AIDEV-NOTE: Dialog for marking commitment as fulfilled with optional feedback
interface FulfillmentDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    commitment: CommitmentWithDetails | null;
    onSuccess?: () => void;
}

export function FulfillmentDialog({
    isOpen,
    onOpenChange,
    commitment,
    onSuccess,
}: FulfillmentDialogProps) {
    const fetcher = useFetcher<FulfillmentFetcherData>();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const revalidator = useRevalidator();
    const [fulfillmentText, setFulfillmentText] = useState("");
    const [hasHandledSuccess, setHasHandledSuccess] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const isSubmitting =
        fetcher.state === "submitting" ||
        (hasSubmitted && navigation.state !== "idle") ||
        (hasSubmitted && !hasHandledSuccess); // Keep loading until dialog closes

    // Reset success handler when dialog opens
    useEffect(() => {
        if (isOpen) {
            setHasHandledSuccess(false);
            setHasSubmitted(false);
        }
    }, [isOpen]);

    const handleSubmit = () => {
        if (!commitment) return;

        setHasSubmitted(true);
        const trimmed = fulfillmentText.trim();
        const payload: {
            intent: "fulfill";
            commitmentId: string;
            fulfillmentText?: string;
        } = {
            intent: "fulfill",
            commitmentId: commitment.id,
        };
        if (trimmed) {
            payload.fulfillmentText = trimmed;
        }

        fetcher.submit(payload, {
            method: "POST",
            // AIDEV-NOTE: Use full action path to ensure it works from any page
            action: "/commitments?index",
            encType: "application/json",
        });
    };

    // Reset state when dialog closes
    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setFulfillmentText("");
        }
        onOpenChange(open);
    };

    // Handle successful submission and trigger revalidation
    useEffect(() => {
        if (
            hasSubmitted &&
            isOpen &&
            !hasHandledSuccess &&
            navigation.state === "idle" &&
            fetcher.state === "idle" &&
            fetcher.data?.success
        ) {
            setHasHandledSuccess(true);
            // AIDEV-NOTE: Invalidate user details cache to update commitments count in sidebar badge
            queryClient.invalidateQueries({ queryKey: ["user"] });
            // AIDEV-NOTE: Trigger Remix revalidation to refetch loader data
            revalidator.revalidate();
            handleOpenChange(false);
            onSuccess?.();
        }
    }, [
        hasSubmitted,
        isOpen,
        hasHandledSuccess,
        navigation.state,
        fetcher.state,
        fetcher.data,
        onSuccess,
        queryClient,
        revalidator,
    ]);

    if (!commitment) return null;

    return (
        <Dialog
            open={isOpen}
            onOpenChange={handleOpenChange}
            analyticsId="fulfillment-dialog"
        >
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Mark Commitment as Fulfilled</DialogTitle>
                    <DialogDescription>
                        Great job on fulfilling this commitment! How did it go?
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Show commitment details */}
                    <div className="rounded-lg bg-muted p-4">
                        <p className="text-sm font-medium">Commitment:</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                            {commitment.content}
                        </p>
                        {commitment.part_interactions?.part && (
                            <p className="mt-2 text-xs text-muted-foreground">
                                Related to:{" "}
                                {commitment.part_interactions.part.name}
                            </p>
                        )}
                    </div>

                    {/* Optional feedback field */}
                    <div className="space-y-2">
                        <Label htmlFor="fulfillment-text">
                            How did it go? (optional)
                        </Label>
                        <Textarea
                            id="fulfillment-text"
                            placeholder="Share your experience, challenges, or insights..."
                            value={fulfillmentText}
                            onChange={(e) => setFulfillmentText(e.target.value)}
                            rows={4}
                            className="resize-none"
                        />
                    </div>

                    {/* Error display */}
                    {fetcher.data?.error && (
                        <p className="text-sm text-destructive">
                            {fetcher.data.error}
                        </p>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} isLoading={isSubmitting}>
                        Mark as Fulfilled
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
